import { ProductTypeEnum } from '@koob/enums';

// Both apps' `TripDesignerProvider` today keep this as plain React state on
// a single ~40-value context, re-rendering all 38 consumers on any change.
// This slice is the future single home for it (US-050/US-052 move each
// app's readers over); no consumer reads or writes it yet (US-046). Never
// persisted (see `tripStorePersist.ts`'s `partializeTripState`).
// The camera saved for a trip: `tripId` says which one it belongs to.
interface MapCamera {
  center: any;
  zoom: any;
  tripId?: string | null;
}

export interface UiSlice {
  selectedDay: number | null;
  selectedType: string | null;
  focusDay: number | null;
  listMode: string;
  mapState: MapCamera;
  // What the map draws right now, as opposed to `mapState` (the camera saved
  // for the trip). `mapZoom` stays null until the map reports one.
  mapZoom: number | null;
  itineraryPinLevel: number;
  searchHotels: boolean;
  compareWithVersion: any;
  compareVersionTotalPrice: any;
  lastAddedExperienceId: string | null;
  loading: boolean;
  selectDay: (dayIndex: number | null, type: string | null) => void;
  resetDaySelection: () => void;
  setFocusDay: (focusDay: number | null) => void;
  setListMode: (listMode: string) => void;
  setMapState: (mapState: MapCamera) => void;
  setMapZoom: (zoom: number | null) => void;
  resetMapView: () => void;
  setItineraryPinLevel: (level: number) => void;
  setSearchHotels: (searchHotels: boolean) => void;
  setCompareWithVersion: (version: any) => void;
  // Takes the new total or an updater from the previous one: the compare
  // panel's days add their price to a running total as each resolves.
  setCompareVersionTotalPrice: (price: any) => void;
  setLastAddedExperienceId: (id: string | null) => void;
  setLoading: (loading: boolean) => void;
}

// Mirrors TripDesignerProvider's own effect today: hotel search opens only
// once a day is selected for a hotel swap.
const createSelectDay =
  (set: any) => (dayIndex: number | null, type: string | null) =>
    set(
      {
        selectedDay: dayIndex,
        selectedType: type,
        searchHotels: type === ProductTypeEnum.HOTEL && dayIndex !== null,
      },
      false,
      'selectDay',
    );

const createResetDaySelection = (set: any) => () =>
  set(
    { selectedDay: null, selectedType: null, searchHotels: false },
    false,
    'resetDaySelection',
  );

export const createUiSlice = (set: any): UiSlice => ({
  selectedDay: null,
  selectedType: null,
  focusDay: null,
  listMode: 'map',
  mapState: { center: null, zoom: null },
  mapZoom: null,
  itineraryPinLevel: 0,
  searchHotels: false,
  compareWithVersion: null,
  compareVersionTotalPrice: null,
  lastAddedExperienceId: null,
  loading: false,

  selectDay: createSelectDay(set),
  resetDaySelection: createResetDaySelection(set),
  setFocusDay: (focusDay: number | null) =>
    set({ focusDay }, false, 'setFocusDay'),
  setListMode: (listMode: string) => set({ listMode }, false, 'setListMode'),
  setMapState: (mapState: { center: any; zoom: any }) =>
    set({ mapState }, false, 'setMapState'),
  setMapZoom: (mapZoom: number | null) => set({ mapZoom }, false, 'setMapZoom'),
  resetMapView: () => set({ mapZoom: null }, false, 'resetMapView'),
  setItineraryPinLevel: (itineraryPinLevel: number) =>
    set({ itineraryPinLevel }, false, 'setItineraryPinLevel'),
  setSearchHotels: (searchHotels: boolean) =>
    set({ searchHotels }, false, 'setSearchHotels'),
  setCompareWithVersion: (compareWithVersion: any) =>
    set({ compareWithVersion }, false, 'setCompareWithVersion'),
  setCompareVersionTotalPrice: (next: any) =>
    set(
      (state: UiSlice) => ({
        compareVersionTotalPrice:
          typeof next === 'function'
            ? next(state.compareVersionTotalPrice)
            : next,
      }),
      false,
      'setCompareVersionTotalPrice',
    ),
  setLastAddedExperienceId: (lastAddedExperienceId: string | null) =>
    set({ lastAddedExperienceId }, false, 'setLastAddedExperienceId'),
  setLoading: (loading: boolean) => set({ loading }, false, 'setLoading'),
});
